import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

const MOOD_THRESHOLDS = new Set([3, 5, 7, 10]);
const ENERGY_THRESHOLDS = new Set([3, 5, 7, 10]);
const AUTOSAVE_DELAY_MS = 700;

const clamp = (value: number): number => Math.min(10, Math.max(1, Math.round(value)));

const moodLabel = (value: number): string => {
  if (value <= 3) return 'Low';
  if (value <= 5) return 'Steady';
  if (value <= 7) return 'Good';
  return 'Great';
};

const energyLabel = (value: number): string => {
  if (value <= 3) return 'Drained';
  if (value <= 5) return 'Low';
  if (value <= 7) return 'Balanced';
  return 'High';
};

interface MoodEnergyCardProps {
  initialMood?: number;
  initialEnergy?: number;
  onAutoSave: (payload: { mood?: number; energy?: number }) => Promise<void> | void;
}

export default function MoodEnergyCard({
  initialMood,
  initialEnergy,
  onAutoSave,
}: MoodEnergyCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mood, setMood] = useState(clamp(initialMood ?? 5));
  const [energy, setEnergy] = useState(clamp(initialEnergy ?? 5));
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const moodRef = useRef(mood);
  const energyRef = useRef(energy);
  const previousMoodRef = useRef(mood);
  const previousEnergyRef = useRef(energy);
  const dirtyMoodRef = useRef(false);
  const dirtyEnergyRef = useRef(false);
  const changeVersionRef = useRef(0);
  const saveInFlightRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const resetStatusTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    moodRef.current = mood;
  }, [mood]);

  useEffect(() => {
    energyRef.current = energy;
  }, [energy]);

  useEffect(() => {
    if (!hasInteracted) {
      setMood(clamp(initialMood ?? 5));
      previousMoodRef.current = clamp(initialMood ?? 5);
    }
  }, [initialMood, hasInteracted]);

  useEffect(() => {
    if (!hasInteracted) {
      setEnergy(clamp(initialEnergy ?? 5));
      previousEnergyRef.current = clamp(initialEnergy ?? 5);
    }
  }, [initialEnergy, hasInteracted]);

  useEffect(() => () => {
    clearTimeout(saveTimerRef.current);
    clearTimeout(resetStatusTimerRef.current);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mediaQuery.matches);
    apply();
    mediaQuery.addEventListener('change', apply);
    return () => mediaQuery.removeEventListener('change', apply);
  }, []);

  const moodPercent = useMemo(() => ((mood - 1) / 9) * 100, [mood]);
  const energyPercent = useMemo(() => ((energy - 1) / 9) * 100, [energy]);

  const maybeHaptic = (kind: 'mood' | 'energy', nextValue: number) => {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
    const previous = kind === 'mood' ? previousMoodRef.current : previousEnergyRef.current;
    const thresholds = kind === 'mood' ? MOOD_THRESHOLDS : ENERGY_THRESHOLDS;
    const crossed = [...thresholds].some((threshold) => (
      (previous < threshold && nextValue >= threshold) || (previous > threshold && nextValue <= threshold)
    ));
    if (crossed) navigator.vibrate(8);
    if (kind === 'mood') previousMoodRef.current = nextValue;
    else previousEnergyRef.current = nextValue;
  };

  const scheduleAutoSave = () => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void runAutoSave();
    }, AUTOSAVE_DELAY_MS);
  };

  const runAutoSave = async () => {
    if (saveInFlightRef.current) return;
    if (!dirtyMoodRef.current && !dirtyEnergyRef.current) return;

    const moodDirtyAtStart = dirtyMoodRef.current;
    const energyDirtyAtStart = dirtyEnergyRef.current;
    const moodAtStart = moodRef.current;
    const energyAtStart = energyRef.current;
    const versionAtStart = changeVersionRef.current;
    const payload = {
      mood: moodDirtyAtStart ? moodAtStart : undefined,
      energy: energyDirtyAtStart ? energyAtStart : undefined,
    };

    saveInFlightRef.current = true;
    setSaveState('saving');
    setSaveError(null);
    try {
      await onAutoSave(payload);
      if (changeVersionRef.current === versionAtStart) {
        if (moodDirtyAtStart) dirtyMoodRef.current = false;
        if (energyDirtyAtStart) dirtyEnergyRef.current = false;
      }
      setSaveState('saved');
      clearTimeout(resetStatusTimerRef.current);
      resetStatusTimerRef.current = setTimeout(() => setSaveState('idle'), 1200);
      if (dirtyMoodRef.current || dirtyEnergyRef.current) scheduleAutoSave();
    } catch (err) {
      setSaveState('error');
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      saveInFlightRef.current = false;
    }
  };

  const onSliderChange = (kind: 'mood' | 'energy', nextValue: number) => {
    const value = clamp(nextValue);
    setHasInteracted(true);
    changeVersionRef.current += 1;
    clearTimeout(resetStatusTimerRef.current);
    setSaveState('idle');
    setSaveError(null);

    if (kind === 'mood') {
      maybeHaptic('mood', value);
      dirtyMoodRef.current = true;
      setMood(value);
    } else {
      maybeHaptic('energy', value);
      dirtyEnergyRef.current = true;
      setEnergy(value);
    }
    scheduleAutoSave();
  };

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      if (!current) {
        clearTimeout(saveTimerRef.current);
      }
      return !current;
    });
  };

  const statusText = saveState === 'saving'
    ? 'Saving...'
    : saveState === 'saved'
      ? 'Saved'
      : saveState === 'error'
        ? 'Save failed'
        : null;

  return (
    <section className="px-3 pt-2 pb-1">
      <div className="mx-auto w-full max-w-md">
        <div
          className="rounded-2xl border"
          style={{
            borderColor: 'var(--color-border)',
            background: 'color-mix(in srgb, var(--color-surface-elevated) 70%, var(--color-background))',
          }}
        >
          <button
            type="button"
            onClick={toggleCollapsed}
            className="w-full px-3 py-2.5 flex items-center justify-between gap-2 text-left"
          >
            <div className="min-w-0">
              <p className="text-xs font-semibold text-[var(--color-text-primary)]">Mood + energy</p>
              <p className="text-[11px] text-[var(--color-text-muted)]">
                {mood} / 10 mood · {energy} / 10 energy
              </p>
            </div>
            <div className="flex items-center gap-2">
              {statusText && (
                <span className="text-[11px] text-[var(--color-text-muted)]">{statusText}</span>
              )}
              {collapsed ? (
                <ChevronDown size={15} className="text-[var(--color-text-muted)]" />
              ) : (
                <ChevronUp size={15} className="text-[var(--color-text-muted)]" />
              )}
            </div>
          </button>

          {!collapsed && (
            <div className="px-3 pb-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <div className="mt-2.5 flex flex-col gap-2.5">
                <Rail
                  label="Mood"
                  value={mood}
                  valueLabel={moodLabel(mood)}
                  percent={moodPercent}
                  onChange={(value) => onSliderChange('mood', value)}
                  gradient="linear-gradient(90deg, #FF6B35 0%, #FFD120 50%, #4BD137 100%)"
                  fillColor="#4BD137"
                  sparkleColor="#FFD120"
                  reduceMotion={reduceMotion}
                />
                <Rail
                  label="Energy"
                  value={energy}
                  valueLabel={energyLabel(energy)}
                  percent={energyPercent}
                  onChange={(value) => onSliderChange('energy', value)}
                  gradient="linear-gradient(90deg, #FF6B35 0%, #FFD120 50%, #4BD137 100%)"
                  fillColor="#4BD137"
                  sparkleColor="#FF6B35"
                  reduceMotion={reduceMotion}
                />
              </div>
              {saveError && (
                <p className="text-[11px] mt-2" style={{ color: 'var(--color-error)' }}>
                  {saveError}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

interface RailProps {
  label: string;
  value: number;
  valueLabel: string;
  percent: number;
  gradient: string;
  fillColor: string;
  sparkleColor: string;
  reduceMotion: boolean;
  onChange: (value: number) => void;
}

function Rail({
  label,
  value,
  valueLabel,
  percent,
  gradient,
  fillColor,
  sparkleColor,
  reduceMotion,
  onChange,
}: RailProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);
  const sparkleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const previousValueRef = useRef(value);

  useEffect(() => () => {
    clearTimeout(sparkleTimerRef.current);
  }, []);

  useEffect(() => {
    if (value === previousValueRef.current) return;
    previousValueRef.current = value;
    setShowSparkle(true);
    clearTimeout(sparkleTimerRef.current);
    sparkleTimerRef.current = setTimeout(() => setShowSparkle(false), 260);
  }, [value]);

  const isAnimated = (isDragging || showSparkle) && !reduceMotion;

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-[var(--color-text-secondary)]">{label}</p>
        <p className="text-[11px] text-[var(--color-text-secondary)]">
          <span className="font-semibold text-[var(--color-text-primary)]">{value}</span> · {valueLabel}
        </p>
      </div>

      <div className="relative mt-1.5 h-11">
        <div
          className="absolute top-1/2 h-7 w-7 -translate-y-1/2 -translate-x-1/2 rounded-full pointer-events-none"
          style={{
            left: `${percent}%`,
            background: `radial-gradient(circle, color-mix(in srgb, ${sparkleColor} 28%, transparent) 0%, transparent 72%)`,
            opacity: isAnimated ? 1 : 0.35,
            transform: `translate(-50%, -50%) scale(${isAnimated ? 1.15 : 1})`,
            transition: reduceMotion ? undefined : 'transform 150ms ease-out, opacity 150ms ease-out',
          }}
        />

        {isAnimated && (
          <>
            <span
              className="absolute h-1.5 w-1.5 rounded-full pointer-events-none animate-ping"
              style={{
                left: `calc(${percent}% - 16px)`,
                top: '32%',
                background: sparkleColor,
              }}
            />
            <span
              className="absolute h-1 w-1 rounded-full pointer-events-none animate-ping"
              style={{
                left: `calc(${percent}% + 12px)`,
                top: '62%',
                background: sparkleColor,
                animationDelay: '120ms',
              }}
            />
          </>
        )}

        <div
          className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full overflow-hidden"
          style={{ opacity: 0.3 }}
        >
          <div className="absolute inset-0" style={{ background: gradient }} />
        </div>
        {percent > 0 && (
          <div
            className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full overflow-hidden"
            style={{
              width: `${percent}%`,
              boxShadow: isAnimated
                ? `0 0 12px color-mix(in srgb, ${fillColor} 45%, transparent)`
                : undefined,
              transition: reduceMotion ? undefined : 'width 120ms ease-out, box-shadow 120ms ease-out',
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                background: gradient,
                backgroundSize: `${10000 / percent}% 100%`,
              }}
            />
          </div>
        )}
        <div
          className="absolute top-1/2 h-[22px] w-[22px] -translate-y-1/2 -translate-x-1/2 rounded-full border-2 pointer-events-none"
          style={{
            left: `${percent}%`,
            borderColor: fillColor,
            background: 'var(--color-background)',
            boxShadow: isAnimated
              ? `0 0 0 8px color-mix(in srgb, ${sparkleColor} 25%, transparent), 0 0 16px color-mix(in srgb, ${sparkleColor} 45%, transparent)`
              : `0 0 0 4px color-mix(in srgb, ${sparkleColor} 12%, transparent)`,
            transition: reduceMotion ? undefined : 'left 120ms ease-out, box-shadow 140ms ease-out',
          }}
        />
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          onPointerDown={() => setIsDragging(true)}
          onPointerUp={() => setIsDragging(false)}
          onPointerCancel={() => setIsDragging(false)}
          className="absolute inset-0 h-11 w-full cursor-pointer opacity-0"
          aria-label={`${label} slider`}
        />
      </div>
    </div>
  );
}
